import React, { useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../App';
import { authedFetch } from '../supabaseClient';

// ── Types ─────────────────────────────────────────────────────────────────────
// Shape mirrors the backend SUMMARY_SCHEMA (insights_config.py). Every section
// item is { text, supporting_kpis }; prose sections hold one item, list
// sections hold an array. benchmark_context is always null in v1.

interface SummaryItem {
  text: string;
  supporting_kpis: string[];
}

interface ExecutiveSummary {
  overall_read: SummaryItem;
  key_positives: SummaryItem[];
  key_risks: SummaryItem[];
  growth_quality: SummaryItem;
  efficiency_profile: SummaryItem;
  retention_cohorts: SummaryItem;
  cac_payback: SummaryItem;
  benchmark_context: SummaryItem | null;
  diligence_questions: SummaryItem[];
  data_caveats: SummaryItem[];
  bottom_line: SummaryItem;
}

interface InsightRun {
  id: string;
  status: string;
  model: string;
  output: ExecutiveSummary | null;
  created_at: string;
  cost_usd: number | null;
  stale?: boolean;
  cached?: boolean;
}


// ── Small presentational pieces ────────────────────────────────────────────────


function ProseSection({ title, item }: { title: string; item: SummaryItem }) {
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">{title}</h4>
      <p className="text-sm text-gray-200 leading-relaxed">{item.text}</p>
    </div>
  );
}

function BulletList({
  title,
  items,
  tone = 'neutral',
}: {
  title: string;
  items: SummaryItem[];
  tone?: 'positive' | 'risk' | 'neutral';
}) {
  const marker =
    tone === 'positive' ? 'text-emerald-400' : tone === 'risk' ? 'text-amber-400' : 'text-gray-500';
  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">{title}</h4>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2">
            <span className={`${marker} text-sm leading-relaxed select-none`}>–</span>
            <div className="flex-1">
              <p className="text-sm text-gray-200 leading-relaxed">{item.text}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NumberedList({ title, items }: { title: string; items: SummaryItem[] }) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">{title}</h4>
      <ol className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-gray-500 text-sm tabular-nums select-none">{i + 1}.</span>
            <p className="text-sm text-gray-200 leading-relaxed flex-1">{item.text}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── Summary body ───────────────────────────────────────────────────────────────

function SummaryBody({ summary }: { summary: ExecutiveSummary }) {
  return (
    <div className="space-y-6">
      {/* Overall read — the lead */}
      <div className="space-y-1">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Overall Read</h4>
        <p className="text-sm text-gray-100 leading-relaxed">{summary.overall_read.text}</p>
      </div>

      {/* Positives / risks side by side on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <BulletList title="Key Positives" items={summary.key_positives} tone="positive" />
        <BulletList title="Key Risks" items={summary.key_risks} tone="risk" />
      </div>

      {/* Thematic sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProseSection title="Growth Quality" item={summary.growth_quality} />
        <ProseSection title="Efficiency Profile" item={summary.efficiency_profile} />
        <ProseSection title="Retention & Cohorts" item={summary.retention_cohorts} />
        <ProseSection title="CAC & Payback" item={summary.cac_payback} />
      </div>

      {/* Diligence questions */}
      <NumberedList title="Suggested Diligence Questions" items={summary.diligence_questions} />

      {/* Data caveats — amber note, only when present */}
      {summary.data_caveats.length > 0 && (
        <div className="rounded border border-amber-900/50 bg-amber-950/20 p-3 space-y-1.5">
          <h4 className="text-xs font-bold text-amber-500 uppercase tracking-wide">Data Caveats</h4>
          <ul className="space-y-1">
            {summary.data_caveats.map((item, i) => (
              <li key={i} className="text-sm text-amber-200/90 leading-relaxed flex gap-2">
                <span className="select-none">–</span>
                <span className="flex-1">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bottom line */}
      <div className="border-t border-gray-800 pt-4 space-y-1">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Bottom Line</h4>
        <p className="text-sm text-gray-100 leading-relaxed">{summary.bottom_line.text}</p>
      </div>
    </div>
  );
}

// ── Metadata footer ────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MetaFooter({ run }: { run: InsightRun }) {
  return (
    <div className="border-t border-gray-800 pt-3 mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
      <span>Generated {formatTimestamp(run.created_at)}</span>
      <span>Model {run.model}</span>
      {run.cost_usd != null && <span>Cost ${run.cost_usd.toFixed(3)}</span>}
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────
// Owns the executive-summary lifecycle for one project: loads the latest run on
// mount, exposes generate/regenerate, and renders every state. canGenerate is
// false for anonymous demo visitors — they see the seeded summary read-only.

// Spinner matching the upload flow (same SVG path, same `spin` keyframe).
// Uses the analyzing-state speed (3s) since generation is a slower AI step.
function SpinnerIcon({ size = 30, speed = '3s' }: { size?: number; speed?: string }) {
  return (
    <svg
      className="text-blue-400"
      style={{ animation: `spin ${speed} linear infinite` }}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}


export default function ExecutiveSummaryPanel({
  projectId,
  canGenerate,
}: {
  projectId: string;
  canGenerate: boolean;
}) {
  const [run, setRun] = useState<InsightRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the latest stored summary (no model call). 204 → no run yet.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRun(null);
    authedFetch(apiUrl('/api/insights/summary/latest', projectId))
      .then(async (res) => {
        if (res.status === 204) return null;
        if (!res.ok) throw new Error('Could not load the latest summary.');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setRun(data);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const generate = useCallback(
    async (force: boolean) => {
      setGenerating(true);
      setError(null);
      try {
        const res = await authedFetch(
          apiUrl('/api/insights/summary/generate', projectId, force ? { force: 'true' } : undefined),
          { method: 'POST' },
        );
        if (!res.ok) {
          let detail = 'Summary generation failed.';
          try {
            const body = await res.json();
            if (body?.detail) detail = body.detail;
          } catch {
            /* non-JSON error body; keep the default */
          }
          throw new Error(detail);
        }
        const data = await res.json();
        setRun(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Summary generation failed.');
      } finally {
        setGenerating(false);
      }
    },
    [projectId],
  );

  if (loading) return <p className="text-gray-500 text-sm">Loading…</p>;

  const summary = run?.status === 'complete' ? run.output : null;

  return (
    <div className="space-y-4">
      {/* Header row: title + action */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-white">Executive Summary</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            A concise diligence read synthesized from this project&apos;s computed KPIs.
          </p>
        </div>
        {canGenerate && (
          <button
            onClick={() => generate(summary != null)}
            disabled={generating}
            className="flex-shrink-0 flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded bg-white text-gray-900 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generating && <SpinnerIcon size={14} />}
            {generating
              ? 'Generating…'
              : summary != null
                ? 'Regenerate'
                : 'Generate Summary'}
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded border border-red-900/50 bg-red-950/20 p-3">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* Stale banner */}
      {summary && run?.stale && (
        <div className="rounded border border-amber-900/50 bg-amber-950/20 px-3 py-2">
          <p className="text-xs text-amber-300">
            The underlying data has changed since this summary was generated.
            {canGenerate ? ' Regenerate for an up-to-date read.' : ''}
          </p>
        </div>
      )}

      {/* Body / empty state */}
      {summary ? (
        <>
          <SummaryBody summary={summary} />
          {run && <MetaFooter run={run} />}
        </>
      ) : (
        !generating && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">
              {canGenerate
                ? 'No summary yet. Generate one to get a fast read on what matters most.'
                : 'No summary is available for this project yet.'}
            </p>
          </div>
        )
      )}

      {/* Generating placeholder (first run, nothing to show yet) — blue
          processing state with the same spinner as the upload flow. */}
      {generating && !summary && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <SpinnerIcon />
          <div className="space-y-1 text-center">
            <p className="text-blue-400 text-sm">Analyzing this project&apos;s KPIs…</p>
            <p className="text-blue-400/60 text-xs">Synthesizing computed metrics into a diligence read</p>
          </div>
        </div>
      )}
    </div>
  );
}
